import { useState, useEffect } from 'react'
import { Grid, Typography, Box } from '@material-ui/core'

import { useFetch, useSnackbar, usePermissions } from 'hooks'

import { Card, LoadingFeedback } from 'components'
import { AddCommision, ListCommision, Kinded } from './components'

import { useForm } from 'react-hook-form'

import * as service from 'service'
import helpers from 'helpers'
import constants from 'constants/index'
import { Alert, AlertTitle } from '@material-ui/lab'

const TransferCommision = ({ seller, refreshSeller, disabled }) => {
  const permissions = usePermissions()
  const snackbar = useSnackbar()
  const [loading, setLoading] = useState(false)

  const { response, isLoading, refresh } = useFetch(
    service.dponet.transferCommisions.index,
    {
      entityId: seller?.id,
      entityType: 'Seller',
      order: 'level desc, id desc',
    },
    [],
  )

  const permitted = permissions.permitted(
    constants.permissions.SELLERS.MANAGE_COMMISSION,
  )

  const PermittedChange = permitted && !disabled
  const displayAlert = !permitted && !disabled

  const lastCommision = seller?.lastTransferCommision
  const rangeTo = lastCommision?.rangeTo || '0'
  const percentage = lastCommision?.percentage ?? 10

  const { handleSubmit, control, errors, watch, reset, setValue } = useForm({
    defaultValues: {
      kind: lastCommision?.kind || 'quantity',
      level: lastCommision ? lastCommision?.level + 1 : 1,
      rangeFrom: lastCommision
        ? helpers.sellers.defaultValues(lastCommision?.kind, rangeTo, 1, false)
        : 0,
      rangeTo: lastCommision
        ? helpers.sellers.defaultValues(lastCommision?.kind, rangeTo, 2, false)
        : 5,
      percentage: lastCommision
        ? helpers.sellers.defaultValues('percentage', percentage, 0.1, false)
        : 10,
    },
  })

  const watchedKind = watch('kind')

  useEffect(() => {
    if (lastCommision) return
    setValue('rangeFrom', 0)
    setValue(
      'rangeTo',
      watchedKind === 'quantity'
        ? 5
        : helpers.formatters.currencyInput('50000'),
    )
  }, [watchedKind])

  useEffect(() => {
    if (lastCommision) return
    reset({
      kind: watchedKind || 'quantity',
      level: 1,
      rangeFrom: 0,
      rangeTo:
        (watchedKind || 'quantity') === 'quantity'
          ? 5
          : helpers.formatters.currencyInput('50000'),
      percentage: 10,
    })
  }, [lastCommision])

  const onSubmit = async (data) => {
    setLoading(true)
    try {
      await service.dponet.transferCommisions.create(
        helpers.sellers.mountTransfer(data, seller?.id),
      )
      snackbar.open({
        message: 'Comissão adicionada com sucesso!',
        variant: 'success',
      })
      refresh()

      const isQuantity = data.kind === 'quantity'
      const rawRangeTo = isQuantity
        ? parseInt(data.rangeTo) || 0
        : helpers.formatters.currencyOutput(data.rangeTo)

      reset({
        kind: data.kind,
        level: parseInt(data.level) + 1,
        rangeFrom: helpers.sellers.defaultValues(
          data.kind,
          rawRangeTo,
          1,
          false,
        ),
        rangeTo: helpers.sellers.defaultValues(
          data.kind,
          rawRangeTo,
          isQuantity ? 11 : 501,
          false,
        ),
        percentage: Math.min(100, parseFloat(data.percentage || 0) + 3),
      })
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(
          error?.response?.data?.error ||
            'Ocorreu um erro ao adicionar a comissão',
        ),
        variant: 'error',
      })
    } finally {
      setLoading(false)
      refreshSeller()
    }
  }

  return (
    <Card title="Opções de níveis">
      <LoadingFeedback isLoading={isLoading || loading} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          {displayAlert && (
            <Grid xs={12} item>
              <Alert variant="outlined" severity="warning">
                <AlertTitle>
                  Você não tem permissão para criar, editar ou excluir
                  comissões.
                </AlertTitle>
                <Typography variant="span">
                  Entre em contato com o administrador ou um colaborador com
                  acesso à configuração dos perfis de usuário.
                </Typography>
              </Alert>
            </Grid>
          )}
          {PermittedChange && (
            <>
              <Grid xs={12} item>
                <Kinded
                  control={control}
                  disabled={disabled || lastCommision}
                />
              </Grid>
              <Grid xs={12} item>
                <Typography variant="h6">
                  Selecionar a faixa do nível por quantidade de clientes
                  (CNPJ/CPF) ou MMR.
                </Typography>
              </Grid>
              <Grid xs={12} item>
                <AddCommision
                  control={control}
                  errors={errors}
                  watch={watch}
                  loading={loading}
                  disabled={disabled}
                />
              </Grid>
            </>
          )}
        </Grid>
      </form>
      <Box mt={PermittedChange ? 4 : 0}>
        <Grid xs={12} item>
          <ListCommision
            commisions={response?.data?.transferCommisions || []}
            refresh={refresh}
            refreshSeller={refreshSeller}
            disabled={!PermittedChange}
            setLoading={setLoading}
          />
        </Grid>
      </Box>
    </Card>
  )
}

export default TransferCommision
